<script setup lang="ts">
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import type { TimelineProps } from "./TimelineProps";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import type { ScrollAnimationType } from "@/apps/page/page-project.interface";

const props = defineProps<{
    block: PageBlock<TimelineProps>;
    pageColors: CssVarColors;
    scrollAnimationType?: ScrollAnimationType;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section
        :id="block.webId"
        :backgroundColor="styleValues.background"
        :scrollAnimationType="scrollAnimationType"
    >
        <FullSpacing>
            <div
                v-if="dataValues.events.data.items.length > 0"
                class="grid grid-cols-1 gap-8 lg:grid-cols-4 lg:overflow-hidden max-w-2xl mx-auto lg:max-w-7xl"
            >
                <div v-for="event in dataValues.events.data.items" :key="event.id">
                    <EditableElement
                        :block-id="block.id"
                        :field-name="`events_${event.id}`"
                        :hideable="false"
                    >
                        <div class="flex flex-col h-full gap-y-6">
                            <div class="flex items-center relative">
                                <div
                                    class="absolute border-t flex-1 lg:hidden -ml-2 mt-[1px] transform -translate-x-full w-full"
                                    :style="{ borderColor: styleValues.border }"
                                ></div>
                                <svg viewBox="0 0 4 4" aria-hidden="true" class="w-1 h-1 mr-4">
                                    <circle r="2" cx="2" cy="2" :fill="styleValues.brand"></circle>
                                </svg>
                                <span class="text-sm" :style="{ color: styleValues.brand }">{{
                                    event.date
                                }}</span>
                                <div
                                    class="hidden lg:block flex-1 border-t mt-[1px] -mr-6 ml-6"
                                    :style="{ borderColor: styleValues.border }"
                                ></div>
                            </div>
                            <div class="flex flex-col gap-y-1">
                                <Paragraph
                                    :text="event.title"
                                    :textColor="styleValues.text"
                                    class="font-semibold text-lg/8"
                                />
                                <Paragraph
                                    :text="event.description"
                                    :textColor="styleValues.text"
                                    class="text-base/7 opacity-70"
                                />
                            </div>
                        </div>
                    </EditableElement>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
